import { Directive, OnInit, signal, inject, HostListener } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { SearchService } from './search.service';
import { FilterService } from './filter.service';
import { PassportService, PassportRecord } from './passport.service';
import { PassportDetailsStore } from './passport-details.store';
import { UiStateService } from './ui-state.service';
import { OfflineStorageService } from './offline-storage.service';
import { CommentsNotificationService } from './comments-notification.service';
import { PassportHelperService } from './passport-helper.service';
import { PersonaRoutingService } from './persona-routing.service';
import { DateTimeHelperService } from './date-time-helper.service';
import { ScrollPositionService } from './scroll-position.service';
import { PassportMainWithDraftDto } from '../interfaces/api-responses';
import { environment } from '../../environments/environment';
import { CommentsPanelService } from './comments-panel.service';
import { HistoryPanelService } from './history-panel.service';

/**
 * Abstract base class for tab-based persona components (Scheduler, Gatehouse)
 * Contains all shared logic for loading, filtering, and displaying passports
 * with tab categorization (load, shunting, transloading)
 */
@Directive()
export abstract class BaseTabPersonaComponent implements OnInit {
  filters: Array<{ id: string; label: string; options: string[] }> = [];
  isLoading = signal(false);
  protected readonly dateTimeHelper = inject(DateTimeHelperService);
  protected readonly scrollPositionService = inject(ScrollPositionService);
  
  // Desktop View State (can be overridden by child classes)
  protected sortColumn: string = '';
  protected sortDirection: 'asc' | 'desc' = 'asc';
  protected currentPage: number = 1;
  protected itemsPerPage: number = 10;
  protected rowsDropdownOpen = false;
  protected isDesktopView = false;
  protected readonly desktopBreakpoint = 768;

  /**
   * Abstract property - child classes must provide their persona name
   */
  protected abstract get personaName(): string;

  /**
   * Abstract property - child classes must provide default persona fallback
   */
  protected abstract get defaultPersona(): string;

  constructor(
    protected readonly http: HttpClient,
    public searchService: SearchService,
    public filterService: FilterService,
    public passports: PassportService,
    public ui: UiStateService,
    public detailsStore: PassportDetailsStore,
    protected readonly router: Router,
    protected readonly offlineStorage: OfflineStorageService,
    public commentsNotification: CommentsNotificationService,
    protected readonly helper: PassportHelperService,
    protected readonly personaRouting: PersonaRoutingService
  ) {}

  ngOnInit(): void {
    // Check if we should skip reload (returning from detail in mobile view)
    if (this.shouldSkipDataReload()) {
      // Just restore scroll position, don't reload data
      this.scrollPositionService.restoreScrollPositionAfterDataLoad(`/${this.personaName}`);
      // Refresh data in the background without disrupting scroll position
      void this.loadPassportsFromAPI({ silent: true, preserveScrollRoute: `/${this.personaName}` });
      return;
    }
    
    void (async () => {
      await this.loadPassportsFromAPI();
      // Restore scroll position after data is loaded and rendered (mobile view only)
      this.scrollPositionService.restoreScrollPositionAfterDataLoad(`/${this.personaName}`);
    })();
  }

  /**
   * Check if we should skip data reload (silent return from detail page)
   * Returns true if:
   * 1. We're in mobile view (where scroll preservation matters)
   * 2. Data already exists in the passport service
   * 3. We're returning from a detail page (not a fresh app load)
   */
  protected shouldSkipDataReload(): boolean {
    // Only skip in mobile view
    if (this.isDesktopView) return false;
    
    // Only skip if data already exists
    if (this.readSignalValue(this.passports.records, []).length === 0) return false;
    
    // Only skip if returning from detail page
    return this.scrollPositionService.isReturning();
  }

  /**
   * Helper method to get count by tab category
   */
  protected getCountByCategory(category?: string): number {
    const records = this.readSignalValue(this.passports.records, []);
    return category ? records.filter(r => r.tabCategory === category).length : records.length;
  }

  get allCount(): number { return this.getCountByCategory(); }
  get loadCount(): number { return this.getCountByCategory('load'); }
  get shuntingCount(): number { return this.getCountByCategory('shunting'); }
  get transloadingCount(): number { return this.getCountByCategory('transloading'); }
  get transferredCount(): number { return this.getCountByCategory('transferred'); }

  /**
   * Get visible records based on search, filters, and tabs
   */
  get visibleRecords(): PassportRecord[] {
    try {
      const query = (this.readSignalValue(this.searchService.filterQuery, '') || '').trim().toLowerCase();
      const hasQuery = query.length > 0;
      const activeFilters = this.readSignalValue(this.filterService.filters, {});
      const selectedTab = this.readSignalValue(this.ui.schedulerTabSelected, 'all');
      
      return this.readSignalValue(this.passports.records, []).filter(rec => {
        // Tab-based filtering
        if (selectedTab !== 'all' && rec.tabCategory !== selectedTab) return false;
        
        const det = this.detailsStore.get(rec.id);
        
        // Apply active filters
        const apnValue = det?.apnName || det?.apn;
        if (!this.helper.matchesFilter(activeFilters, 'apnName', apnValue)) return false;
        if (!this.helper.matchesFilter(activeFilters, 'haulier', det?.haulier)) return false;
        const destinationValue = det?.destinationWeighbridge || det?.destination;
        if (!this.helper.matchesFilter(activeFilters, 'destination', destinationValue)) return false;
        
        // Apply search query
        if (!hasQuery) return true;
        const haystack = this.helper.buildSearchFields(rec, det).join(' ').toLowerCase();
        return haystack.includes(query);
      });
    } catch { return []; }
  }

  trackById = PassportService.trackById;

  onRefresh(): void {
    void this.loadPassportsFromAPI();
    // Refresh comment notification count
    void this.commentsNotification.refresh();
  }

  onNavChange(id: string): void {
    this.personaRouting.handleNavigation(id, this.personaName);
  }

  /**
   * Load passports from API
   * Child classes can override storePassportDetails() to customize storage
   */
  protected async loadPassportsFromAPI(options?: { silent?: boolean; preserveScrollRoute?: string }): Promise<void> {
    if (this.isLoading()) return;

    const isSilent = options?.silent === true;
    const preserveScrollRoute = options?.preserveScrollRoute;

    if (!isSilent) {
      this.isLoading.set(true);
    }

    // Only save scroll position when NOT silent - silent refresh should preserve existing position
    if (preserveScrollRoute && !isSilent) {
      this.scrollPositionService.saveScrollPosition(preserveScrollRoute);
    }

    try {
      const userID = localStorage.getItem('userID');
      // FIX: Use component's personaName instead of localStorage to ensure correct roleId
      // This prevents issues when user navigates directly via URL (localStorage might have old persona)
      const persona = this.personaName || this.defaultPersona;
      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType') || 'Bearer';
      if (!token) {
        console.warn('No access token found, skipping passport load');
        return;
      }

      const roleId = this.helper.getRoleIdForPersona(persona);
      console.log(`📋 Loading passports with persona: '${persona}', roleId: ${roleId}`);
      const apiUrl = `${environment.apiBaseUrl}/api/v1/passports?userId=${userID}&roleId=${roleId}`;
      const headers = { 'Authorization': `${tokenType} ${token}`, 'Content-Type': 'application/json' };
      const passports = await firstValueFrom(this.http.get<PassportMainWithDraftDto[]>(apiUrl, { headers }));

      // Map passports to frontend records with tab category based on statusCode
      const frontendRecords: PassportRecord[] = passports.map(p => {
        const tabCategory = this.helper.mapStatusCodeToTab(p.statusCode || '');
        return {
          id: p.passportID.toString(),
          status: 'pending', // Keep for compatibility, but tab category is stored separately
          isSynced: true,
          tabCategory: tabCategory,
          statusCode: p.statusCode || ''
        };
      });
      this.passports.records.set(frontendRecords);

      // Store passport details (can be overridden by child classes)
      this.storePassportDetails(passports);

      // Cache data (can be overridden by child classes)
      await this.cacheOfflineData(headers);

      this.rebuildFilters();
    } finally {
      if (!isSilent) {
        this.isLoading.set(false);
      }
      // Only restore scroll position when NOT silent - silent refresh should preserve existing position
      if (preserveScrollRoute && !isSilent) {
        this.scrollPositionService.restoreScrollPositionAfterDataLoad(preserveScrollRoute);
      }
    }
  }

  /**
   * Store passport details in the details store
   * Override in child classes to add persona-specific fields
   */
  protected storePassportDetails(passports: PassportMainWithDraftDto[]): void {
    for (const p of passports) {
      this.detailsStore.upsert({
        id: p.passportID.toString(),
        loadNumber: p.loadNumber || '',
        vendorName: p.vendorName || '',
        vendorNumber: p.vendorNumber || '',
        apn: p.apn || '',
        apnName: p.apnName || '',
        variety: p.variety || '',
        destination: p.destination || '',
        destinationWeighbridge: p.destinationWeighbridge || '',
        origin: this.helper.formatOriginWithType(p.originName, p.rawSourceType),
        bulkerNumber: p.trailerNumber || '',
        bulkerMain: p.trailerMain || '',
        dueDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.dueDateTime || p.scheduledDateTime || ''),
        haulier: p.haulierName || '',
        plantID: p.plantID || (p as any).PlantID,
        statusCode: p.statusCode || '',
        transferFlag: p.transferFlag ?? false
      });
    }
  }

  /**
   * Cache data for offline use
   * Override in child classes to add persona-specific caching
   */
  protected async cacheOfflineData(headers: any): Promise<void> {
    // Shunting yards caching removed - only Gatehouse and Scheduler personas need it
    // and they load it dynamically per passport in passport-details page
  }

  /**
   * Rebuild filters based on current passport records
   */
  protected rebuildFilters(): void {
    try {
      const records = this.readSignalValue(this.passports.records, []);
      this.filters = this.helper.buildOriginDestinationApnFilters(records, this.detailsStore);
    } catch {
      this.filters = this.helper.getDefaultOriginDestinationApnFilters();
    }
  }

  /**
   * Get Math object for template access
   */
  get Math() { return Math; }

  /**
   * Calculate total pages for pagination
   */
  protected getTotalPages(recordsLength: number): number {
    return this.helper.getTotalPages(recordsLength, this.itemsPerPage);
  }

  /**
   * Handle column sorting
   */
  onSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  /**
   * Handle page change
   */
  onPageChange(page: number): void {
    const maxPages = this.getTotalPages(this.getVisibleRecordsForPagination().length);
    if (page >= 1 && page <= maxPages) {
      this.currentPage = page;
    }
  }

  /**
   * Toggle rows dropdown
   */
  toggleRowsDropdown(event?: Event): void {
    if (event) event.stopPropagation();
    this.rowsDropdownOpen = !this.rowsDropdownOpen;
  }

  /**
   * Set items per page
   */
  setItemsPerPage(count: number): void {
    this.itemsPerPage = count;
    this.currentPage = 1; // Reset to first page
    this.rowsDropdownOpen = false;
  }

  /**
   * Close dropdowns on document click
   */
  @HostListener('document:click')
  closeDropdowns(): void {
    this.rowsDropdownOpen = false;
  }

  /**
   * Update desktop view state based on window width
   */
  protected updateDesktopViewState(force = false): void {
    const nextValue = globalThis.window === undefined ? false : globalThis.window.innerWidth >= this.desktopBreakpoint;
    const wasDesktop = this.isDesktopView;
    const viewChanged = force || nextValue !== this.isDesktopView;
    if (viewChanged) {
      this.isDesktopView = nextValue;
    }
    if (this.isDesktopView && (force || !wasDesktop)) {
      this.applyDesktopDefaultSort();
    }
    if (viewChanged) {
      this.rebuildFilters();
    }
  }

  protected applyDesktopDefaultSort(): void {
    if (!this.sortColumn) {
      this.sortColumn = 'scheduled';
      this.sortDirection = 'asc';
    }
  }

  /**
   * Handle window resize
   */
  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateDesktopViewState();
  }

  /**
   * Get visible records for pagination calculation
   * Override in child classes if needed
   */
  protected getVisibleRecordsForPagination(): PassportRecord[] {
    return this.visibleRecords;
  }

  protected readSignalValue<T>(valueOrSignal: T | (() => T), fallback: T): T {
    try {
      if (typeof valueOrSignal === 'function') {
        const v = (valueOrSignal as () => T)();
        return (v ?? fallback);
      }
      return valueOrSignal ?? fallback;
    } catch {
      return fallback;
    }
  }

  /**
   * Build panel header object for comments/history panels
   * Override in child classes to add persona-specific fields
   */
  protected buildPanelHeader(id: string): { loadNumber: string; variety: string; haulier: string; scheduledText: string; origin: string; destination: string; apnName: string; vendorName: string; vendorNumber: string; bulkerNumber: string } | undefined {
    const details = this.detailsStore.get(id);
    if (!details) return undefined;
    
    return {
      loadNumber: details.loadNumber || '',
      variety: details.variety || '',
      haulier: details.haulier || '',
      scheduledText: this.dateTimeHelper.formatScheduledText(details.dueDateTime || '') || '',
      origin: details.origin || '',
      destination: details.destinationWeighbridge || details.destination || '',
      apnName: details.apnName || '',
      vendorName: details.vendorName || '',
      vendorNumber: details.vendorNumber || '',
      bulkerNumber: this.helper.getDisplayBulker(details, true)
    };
  }

  /**
   * Open comments panel
   * Override in child classes if needed
   */
  protected openComments(id: string, commentsPanelService?: CommentsPanelService): void {
    const header = this.buildPanelHeader(id);
    if (header && commentsPanelService) {
      commentsPanelService.openCommentsPanel(id, header);
    }
  }

  /**
   * Open history panel
   * Override in child classes if needed
   */
  protected openHistory(id: string, historyPanelService?: HistoryPanelService): void {
    const header = this.buildPanelHeader(id);
    if (header && historyPanelService) {
      historyPanelService.openHistoryPanel(id, header);
    }
  }

  /**
   * Determine the visual style state for a passport based on its properties
   */
  getStyleState(passportId: string): 'default' | 'pcr-change' | 'cancelled' | 'state-2' {
    const details = this.detailsStore.get(passportId);
    return this.helper.getStyleState(details);
  }

  /**
   * Get sort values for a given column
   * Override in child classes to add persona-specific sort columns
   */
  protected getSortValues(column: string, a: PassportRecord, b: PassportRecord, aDetails: any, bDetails: any): { valA: any; valB: any } {
    return this.helper.getSortValues(column, a, b, aDetails, bDetails);
  }

  /**
   * Compare two values for sorting
   */
  protected compareValues(a: any, b: any, direction: 'asc' | 'desc'): number {
    return this.helper.compareValues(a, b, direction);
  }

  /**
   * Get sorted and paginated records
   * Override in child classes to add persona-specific sort columns
   */
  get sortedPaginatedRecords(): PassportRecord[] {
    let records = [...this.getVisibleRecordsForPagination()];

    // Sorting
    if (this.sortColumn) {
      records.sort((a, b) => {
        const aDetails = this.detailsStore.get(a.id);
        const bDetails = this.detailsStore.get(b.id);
        const { valA, valB } = this.getSortValues(this.sortColumn, a, b, aDetails, bDetails);
        return this.compareValues(valA, valB, this.sortDirection);
      });
    }

    // Pagination
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return records.slice(startIndex, startIndex + this.itemsPerPage);
  }
}

